import { CODES } from '@/consts/codes';
import type { MenuItem } from '@/consts/navigation';
import { ROUTES } from '@/consts/routes';
import type { TranslationKey } from '@/i18n/i18n';

// --- Override types ---

interface OrderOverride {
  /** Code of the item to reorder (e.g. 'chartering', 'voy.calc.dry') */
  target: string;
  order: number;
}

interface ComponentOverride {
  /** Code of the item to replace with a custom component */
  target: string;
  component: React.ComponentType<{ item: MenuItem }>;
}

interface InsertOverride {
  /** Code of the parent section to insert into (e.g. 'chartering') */
  parent: string;
  insert: Omit<MenuItem, 'text'> & { text: TranslationKey };
}

interface GroupOverride {
  /** Group label displayed as section header (e.g. 'OPERATIONS') */
  group: TranslationKey;
  /** Codes of top-level items to pull into this group. Use 'text:...' prefix to match by text instead of code. */
  items: string[];
  /** Sort order of the group among other top-level items */
  order: number;
  /** When true and the group has one item, promote that item's children directly under the group label. */
  promoteChildren?: boolean;
}

export type MenuOverride = OrderOverride | ComponentOverride | InsertOverride | GroupOverride;

function isOrderOverride(o: MenuOverride): o is OrderOverride {
  return 'target' in o && 'order' in o && !('component' in o);
}

function isComponentOverride(o: MenuOverride): o is ComponentOverride {
  return 'target' in o && 'component' in o;
}

function isInsertOverride(o: MenuOverride): o is InsertOverride {
  return 'parent' in o && 'insert' in o;
}

function isGroupOverride(o: MenuOverride): o is GroupOverride {
  return 'group' in o && 'items' in o;
}

// --- Override config ---
// Edit this array to control ordering, inject items, group items, or replace with custom components.
//
// Use code (e.g. 'chartering') to target items by their Code field.
// Use 'text:...' prefix (e.g. 'text:Operations') to target legacy items by their display Name.

export const menuOverrides: MenuOverride[] = [
  // --- Inject native React pages into legacy sections ---
  {
    parent: 'acc.payments',
    insert: {
      text: 'navigation.acc.children.paymentOrders' as TranslationKey,
      to: ROUTES.acc.paymentOrders,
      code: CODES.acc.paymentOrders,
      hidden: true
    }
  },
  {
    parent: 'chartering',
    insert: {
      text: 'navigation.chartering.children.estimatorNew' as TranslationKey,
      to: ROUTES.chartering.estimatorNew,
      code: CODES.voy.estimatorNew,
      order: 15
    }
  },
  {
    parent: 'chartering',
    insert: {
      text: 'navigation.chartering.children.marketIndices' as TranslationKey,
      to: ROUTES.chartering.marketIndices,
      code: CODES.voy.marketIndices,
      order: 17
    }
  },
  // --- Inject tenant settings into legacy Administration section (ADM only) ---
  {
    parent: 'admin',
    insert: {
      text: 'navigation.admin.children.tenantSettings' as TranslationKey,
      to: ROUTES.admin.tenantSettings,
      code: CODES.admin.tenantSettings,
      order: 1
    }
  },
  // --- Top-level groups ---
  {
    group: 'Commercial' as TranslationKey,
    items: ['chartering', 'text:Operations', 'text:Market Data', 'text:Bunkering'],
    order: 30
  },
  {
    group: 'Finance' as TranslationKey,
    items: ['text:Accounting'],
    order: 40
  },
  {
    group: 'Technical' as TranslationKey,
    items: ['pms', 'pur'],
    order: 50
  },
  {
    group: 'Administration' as TranslationKey,
    items: ['admin'],
    order: 60,
    promoteChildren: true
  },
  {
    group: 'Other' as TranslationKey,
    items: ['text:Port Expenses', 'text:Freight', 'text:Vessel Map', 'text:Commercial'],
    order: 70
  }
];

// --- Apply logic ---

/**
 * Apply overrides to a merged menu tree:
 * 1. Build groups — pull matching top-level items into group wrappers
 * 2. Apply order overrides by code
 * 3. Apply component overrides by code
 * 4. Insert new items into parent sections
 * 5. Sort all levels by order
 */
export function applyMenuOverrides(menu: MenuItem[], overrides: MenuOverride[]): MenuItem[] {
  if (overrides.length === 0) return assignOrdersAndSort(menu);

  // Separate override types
  const orderMap = new Map<string, number>();
  const componentMap = new Map<string, React.ComponentType<{ item: MenuItem }>>();
  const inserts = new Map<string, MenuItem[]>();
  const groups: GroupOverride[] = [];

  for (const o of overrides) {
    if (isGroupOverride(o)) {
      groups.push(o);
    } else if (isOrderOverride(o)) {
      orderMap.set(o.target, o.order);
    } else if (isComponentOverride(o)) {
      componentMap.set(o.target, o.component);
    } else if (isInsertOverride(o)) {
      const existing = inserts.get(o.parent) ?? [];
      existing.push(o.insert);
      inserts.set(o.parent, existing);
    }
  }

  // Step 1: Apply groups — pull items into group wrappers
  let items = menu;
  if (groups.length > 0) {
    items = applyGroups(items, groups);
  }

  // Step 2: Apply order, component, insert overrides recursively
  function process(menuItems: MenuItem[]): MenuItem[] {
    const withOverrides = menuItems.map((item, i) => {
      const code = item.code;
      const explicitOrder = code ? orderMap.get(code) : undefined;
      const order = explicitOrder ?? item.order ?? (i + 1) * 10;
      const component = code ? (componentMap.get(code) ?? item.component) : item.component;

      let children = item.children;

      // Inject new children if this item is a target parent
      if (code && inserts.has(code)) {
        const inserted = inserts.get(code);
        if (inserted) {
          children = [...(children ?? []), ...inserted];
        }
      }

      // Recurse into children
      if (children && children.length > 0) {
        children = process(children);
      }

      return { ...item, order, component, children };
    });

    return withOverrides.sort((a, b) => a.order - b.order);
  }

  return process(items);
}

/**
 * Extract non-navigable sub-groups from within items and promote them as siblings.
 * E.g. "Voyage Tasks" inside "Operations" gets moved to the same level as "Operations".
 * Items whose children are ALL sub-groups (like "Office Applications") are fully dissolved.
 */
function promoteSubGroups(items: MenuItem[]): MenuItem[] {
  const result: MenuItem[] = [];
  for (const item of items) {
    if (!item.children || item.children.length === 0) {
      result.push(item);
      continue;
    }

    const keepChildren: MenuItem[] = [];
    const promoted: MenuItem[] = [];

    for (const child of item.children) {
      const childHasUrl = child.to !== undefined && child.to !== '';
      const childHasChildren = child.children !== undefined && child.children.length > 0;

      if (!childHasUrl && childHasChildren) {
        promoted.push(child);
      } else {
        keepChildren.push(child);
      }
    }

    // Keep parent if it still has navigable children
    if (keepChildren.length > 0) {
      result.push({ ...item, children: keepChildren });
    }

    // Place promoted sub-groups at this level
    result.push(...promoted);
  }
  return result;
}

/**
 * Pull matching top-level items into group wrappers.
 * Items matched by a group are removed from the flat list and placed under a
 * group MenuItem (no `to`, has `children`) which renders as a NavMenuItemGroup header.
 */
function applyGroups(menu: MenuItem[], groups: GroupOverride[]): MenuItem[] {
  // Build a set of all item identifiers claimed by groups
  const claimedCodes = new Set<string>();
  const claimedTexts = new Set<string>();
  for (const g of groups) {
    for (const id of g.items) {
      if (id.startsWith('text:')) {
        claimedTexts.add(id.slice(5));
      } else {
        claimedCodes.add(id);
      }
    }
  }

  // Partition: items claimed by groups vs unclaimed
  const itemByCode = new Map<string, MenuItem>();
  const itemByText = new Map<string, MenuItem>();
  const unclaimed: MenuItem[] = [];

  for (const item of menu) {
    const code = item.code;
    // Use translation key text as fallback identifier for legacy items
    const text = item.text as string;

    if (code && claimedCodes.has(code)) {
      itemByCode.set(code, item);
    } else if (claimedTexts.has(text)) {
      itemByText.set(text, item);
    } else {
      unclaimed.push(item);
    }
  }

  // Build group MenuItems
  const groupItems: MenuItem[] = groups.map((g) => {
    const children: MenuItem[] = [];
    for (const id of g.items) {
      let item: MenuItem | undefined;
      if (id.startsWith('text:')) {
        item = itemByText.get(id.slice(5));
      } else {
        item = itemByCode.get(id);
      }
      if (item) children.push(item);
    }

    // When promoteChildren is set and there's exactly one module, use its children directly
    // and promote nested sub-groups (no URL) to the same level
    if (g.promoteChildren && children.length === 1 && children[0]?.children) {
      return {
        text: g.group,
        children: promoteSubGroups(children[0].children),
        order: g.order,
        isGroup: true
      } satisfies MenuItem;
    }

    return {
      text: g.group,
      children,
      order: g.order,
      isGroup: true
    } satisfies MenuItem;
  });

  // Return: unclaimed items + group wrappers, all at top level
  return [...unclaimed, ...groupItems];
}

/** Assign auto-order and sort (used when no overrides exist). */
function assignOrdersAndSort(items: MenuItem[]): MenuItem[] {
  return items.map((item, i) => {
    const order = item.order ?? (i + 1) * 10;
    const children = item.children ? assignOrdersAndSort(item.children) : undefined;
    return { ...item, order, children };
  });
}
